import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import MentionTextarea from '@/components/creative/MentionTextarea';

interface Props {
  open: boolean;
  title: string;
  message?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;          // textarea instead of single-line input
  /** Let the writer name a colleague with @. Only for notes another person reads:
   *  a rejection, a hand-back, a reason for putting a piece on hold. The ids come
   *  back as the second argument to onConfirm and the people named are told. */
  mentions?: boolean;
  /** Mentions the text already carried, so editing a message does not drop them. */
  defaultMentions?: string[];
  maxLength?: number;
  /** the text may be left empty (the answer is already elsewhere, e.g. notes on the files) */
  allowEmpty?: boolean;
  onConfirm: (value: string, mentioned: string[]) => void | Promise<void>;
  onCancel: () => void;
}

export default function PromptDialog({
  open, title, message,
  placeholder, defaultValue = '',
  confirmLabel = 'OK', cancelLabel = 'Cancel',
  multiline = false, maxLength, allowEmpty = false,
  mentions = false, defaultMentions,
  onConfirm, onCancel,
}: Props) {
  const [value, setValue] = useState(defaultValue);
  const [mentioned, setMentioned] = useState<string[]>(defaultMentions ?? []);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Reset + autofocus on open
  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setMentioned(defaultMentions ?? []);
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ESC closes; Enter submits (single-line only - in textarea Enter inserts newline)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
      if (e.key === 'Enter' && !multiline && !busy) handleConfirm();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy, value]);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm(value, mentioned);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => !busy && onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-slate-800 bg-bg-card shadow-2xl"
      >
        <div className="flex items-start gap-3 p-5">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-muted-strong">{title}</h3>
            {message && <div className="text-sm text-muted mt-1.5 leading-relaxed">{message}</div>}
            {multiline && mentions ? (
              <div className="mt-3">
                <MentionTextarea
                  value={value}
                  onChange={setValue}
                  mentions={mentioned}
                  onMentionsChange={setMentioned}
                  placeholder={placeholder}
                  rows={4}
                  disabled={busy}
                  textareaRef={inputRef as React.RefObject<HTMLTextAreaElement | null>}
                />
                <p className="text-[10px] text-muted mt-1">Type @ to name someone. They are told.</p>
              </div>
            ) : multiline ? (
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={4}
                disabled={busy}
                className="mt-3 w-full text-sm bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-muted-strong placeholder:text-muted/50 focus:outline-none focus:border-accent-400 disabled:opacity-50"
              />
            ) : (
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={busy}
                className="mt-3 w-full h-10 text-sm bg-slate-950 border border-slate-800 rounded-lg px-3 text-muted-strong placeholder:text-muted/50 focus:outline-none focus:border-accent-400 disabled:opacity-50"
              />
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-muted hover:text-muted-strong p-1 -m-1 rounded disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-800 bg-slate-900/30 rounded-b-xl">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button size="sm" onClick={handleConfirm} loading={busy} disabled={!allowEmpty && !value.trim()}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
